import { useMemo } from 'react';

import { ForgeMergeCheckResult } from 'src/types/custom-merge-checks';

import { useMergeChecks } from '../../components/merge-checks-card';
import { usePollingResults } from '../../components/merge-checks-card/hooks';
import {
  customCheckResultAllowsMerge,
  isCustomCheckRequiredAndFailed,
} from '../../components/merge-checks-card/utils';

import { CustomMergeCheckData } from './types';

export const useCustomMergeData = (): CustomMergeCheckData => {
  const {
    data: resultsData,
    error: resultsError,
    refresh: resultsRefresh,
  } = usePollingResults();
  const [{ isMergeChecksFetchRetrying }, _] = useMergeChecks();

  const customMergeCheckResults = resultsData?.results;
  const customMergeCheckResultsLoading =
    !customMergeCheckResults || isMergeChecksFetchRetrying;

  const {
    preMergeCheckResults,
    onMergeCheckResults,
    requiredCustomOnMergeChecksFailedCount,
  } = useMemo(
    () =>
      (customMergeCheckResults ?? []).reduce(
        (acc, result) => {
          if (result.checkType === 'pre-merge') {
            acc.preMergeCheckResults.push(result);
          } else if (result.checkType === 'on-merge') {
            acc.onMergeCheckResults.push(result);
            // We need to use isCustomCheckRequiredAndFailed here instead of
            // customCheckResultAllowsMerge because the other function returns false when the check
            // is running. We only want to count the ones that have finished and failed.
            if (isCustomCheckRequiredAndFailed(result)) {
              acc.requiredCustomOnMergeChecksFailedCount++;
            }
          }
          return acc;
        },
        {
          preMergeCheckResults: [] as ForgeMergeCheckResult[],
          onMergeCheckResults: [] as ForgeMergeCheckResult[],
          requiredCustomOnMergeChecksFailedCount: 0,
        }
      ),
    [customMergeCheckResults]
  );

  const failedPreMergeChecksCount = useMemo(
    () =>
      preMergeCheckResults.filter(
        ({ status }) => status === 'FAILED' || status === 'ERROR'
      ).length,
    [preMergeCheckResults]
  );

  const allRequiredPreMergeChecksPassed = useMemo(
    () =>
      preMergeCheckResults.every(result =>
        customCheckResultAllowsMerge(result)
      ),
    [preMergeCheckResults]
  );

  const customMergeChecksIsMergeable =
    (!customMergeCheckResultsLoading && allRequiredPreMergeChecksPassed) ||
    // we want a fallback behaviour to enable the merge button if the results endpoint returns an error to unblock
    // users as we ensure all checks have passed upon a merge attempt in the backend anyway
    !!resultsError;

  return {
    customMergeChecksIsMergeable,
    customMergeChecksError: resultsError,
    requiredCustomOnMergeChecksFailedCount,
    forgeMergeChecksData: {
      enabledOnMergeChecks: {
        // This data is read by the merge dialog to render the list of on-merge
        // checks that will run. We override the status to IMPENDING to ensure
        // that the checks always render with the same icon, even if the check
        // had already run on a previous merge attempt. This is very hacky and
        // should be refactored at some point.
        enabledChecks: onMergeCheckResults.map(result => ({
          ...result,
          status: 'IMPENDING',
        })),
        refresh: resultsRefresh,
        error: resultsError,
      },
      mergeChecksResultsLoading: customMergeCheckResultsLoading,
      failedPreMergeChecksCount,
      preMergeChecksCount: preMergeCheckResults.length,
    },
  };
};
